// @flow
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useMutation } from 'urql';

import { Route } from '@ta/lithium.context';
import InteractionMutation from './InteractionMutation.gql';
import { type Step, type Responses } from './useExplicitPreferencesState';

const getTrackingQuestionType = (step: Step) => {
  switch (step) {
    case 'HAS_DESTINATION':
      return 'WELCOME_SCREEN';
    case 'DESTINATION':
      return 'DESTINATION';
    case 'DATES':
      return 'DATES';
    case 'SEGMENT':
      return 'COMPANION';
    case 'INTERESTS':
      return 'INTERESTS';
    case 'PROFILE':
      return 'ACCOUNT_SCREEN';
    case 'END_SCREEN':
      return 'END_SCREEN';
    default:
      throw new Error(`Unknown step: ${step}`);
  }
};

const getTrackingResponseType = (step: Step, responses: Responses) => {
  switch (step) {
    case 'HAS_DESTINATION':
      if (responses.hasDestination) {
        return 'HAS_DESTINATION';
      }
      return 'HAS_NO_DESTINATION';
    case 'DESTINATION':
      if (responses.isInDestination) {
        return 'NEARBY';
      }
      return 'GEO_SELECTION';
    case 'DATES':
      if (responses.flexibleDates) {
        return 'FUZZY_DATES';
      }
      return 'HARD_DATES';
    case 'SEGMENT':
      return responses.segment;
    case 'INTERESTS':
      return 'INTERESTS_SELECTED';
    case 'PROFILE':
      if (responses.firstName && responses.homeGeoId) {
        return 'NAME_AND_HOMETOWN_INPUT';
      } else if (responses.firstName) {
        return 'NAME_INPUT';
      } else if (responses.homeGeoId) {
        return 'HOMETOWN_INPUT';
      }
      break;
    case 'END_SCREEN':
      return null;
    default:
      throw new Error(`Unknown step: ${step}`);
  }
  return null;
};

const getContext = (mode: string, hasExistingResponses: boolean) => {
  if (mode === 'EDITING') {
    if (!hasExistingResponses) {
      return 'ADDING';
    }
    return 'EDITING';
  } else if (mode === 'GEOPILL') {
    return 'GEOPILL';
  }
  // This includes mode === ONETAP
  return 'ONBOARDING';
};

const useExplicitPreferencesInteraction = (
  step: Step,
  responses: Responses,
  hasExistingResponses: boolean,
  mode: string,
) => {
  const [, { uid }] = useContext(Route);

  const [, mutateInteraction] = useMutation(InteractionMutation);
  const context = useRef(getContext(mode, hasExistingResponses)).current;

  const question = getTrackingQuestionType(step);
  const responseType = getTrackingResponseType(step, responses);
  const interestIds = responses.interests;

  // Log mutation of the question being shown when the step changes (context and uid should be constant)
  useEffect(() => {
    mutateInteraction({
      interaction: {
        explicitPreferences: {
          showQuestion: {
            context,
            question,
            uid,
          },
        },
      },
    });
  }, [context, uid, question, mutateInteraction]);

  const trackRespondQuestion = useCallback(() => {
    mutateInteraction({
      interaction: {
        explicitPreferences: {
          respondQuestion: {
            context,
            question,
            response: responseType,
            uid,
            interestIds: responseType === 'INTERESTS_SELECTED' ? interestIds : null,
          },
        },
      },
    });
  }, [uid, question, responseType, interestIds, context, mutateInteraction]);

  const trackSkipQuestion = useCallback(() => {
    mutateInteraction({
      interaction: {
        explicitPreferences: {
          navigatesQuestion: {
            context,
            question,
            action: 'SKIP',
            uid,
          },
        },
      },
    });
  }, [uid, question, context, mutateInteraction]);

  const trackGoBackQuestion = useCallback(() => {
    mutateInteraction({
      interaction: {
        explicitPreferences: {
          navigatesQuestion: {
            context,
            question,
            action: 'BACK',
            uid,
          },
        },
      },
    });
  }, [uid, question, context, mutateInteraction]);

  const trackCloseModal = useCallback(() => {
    mutateInteraction({
      interaction: {
        explicitPreferences: {
          navigatesQuestion: {
            context,
            question,
            action: 'CLOSE',
            uid,
          },
        },
      },
    });
  }, [uid, question, context, mutateInteraction]);

  return {
    trackRespondQuestion,
    trackSkipQuestion,
    trackGoBackQuestion,
    trackCloseModal,
  };
};

export default useExplicitPreferencesInteraction;
